import { LabelIds, Perspectives, RowOffsets, Stages } from "../consts";
import type {
  LabelId,
  LabelRef,
  PriceFacts,
  PriceView,
  RowOffset,
} from "../types";

/**
 * Offer-pending indicator offset for inbox / UK “new total” dots.
 */
const pendingOfferOffset = (_facts: PriceFacts, view: PriceView): RowOffset =>
  view.hasPendingOffer === true ? RowOffsets.Indicator : RowOffsets.None;

/**
 * DIY dated space rental: "Space rental for {hours} hours".
 */
const diySpaceRentalLabel = (facts: PriceFacts): LabelRef => ({
  id: LabelIds.DiySpaceRental,
  args: [facts.durationHours ?? 0],
});

/**
 * DIY dated space rental, PreCheckout/Checkout variant:
 * "{hourlyPrice} x {hours} hours".
 */
const diySpaceRentalRateLabel = (facts: PriceFacts): LabelRef => ({
  id: LabelIds.DiySpaceRentalRate,
  args: [{ money: facts.hourlyRate ?? 0 }, facts.durationHours ?? 0],
});

/**
 * FSV dated space rental: room type for {hours} hrs.
 */
const fsvSpaceRentalLabel = (facts: PriceFacts): LabelRef => ({
  id: LabelIds.FsvSpaceRental,
  args: [facts.roomType ?? "", facts.durationHours ?? 0],
});

/**
 * Booking total label (Rules Sheet 13A/13B):
 * 1. pending offer → New Total
 * 2. taxes unknown → Total before taxes
 * 3. host → Guest total
 * 4. else Total
 */
const bookingTotalLabel = (facts: PriceFacts, view: PriceView): LabelRef => {
  if (view.hasPendingOffer === true) {
    return { id: LabelIds.NewTotal };
  }
  if (facts.taxes === null) {
    return { id: LabelIds.TotalBeforeTaxes };
  }
  if (view.perspective === Perspectives.Host) {
    return { id: LabelIds.GuestTotal };
  }
  return { id: LabelIds.Total };
};

/**
 * Host OfferFlow FSV: "Includes {$x} due at venue" under the total.
 * MoneyArg keeps caption currency formatting in sync with the value column.
 */
const bookingTotalCaption = (
  facts: PriceFacts,
  view: PriceView,
): LabelRef | undefined => {
  if (
    view.perspective !== Perspectives.Host ||
    view.stage !== Stages.Offer ||
    facts.isDiy ||
    facts.dueAtVenue == null ||
    facts.dueAtVenue <= 0
  ) {
    return undefined;
  }

  return {
    id: LabelIds.IncludesDueAtVenue,
    args: [{ money: facts.dueAtVenue }],
  };
};

/**
 * Food & drink minimum label; optional `foodDrinkType` passed as args for
 * bindings that specialize copy (food / drink / both).
 */
const foodDrinkMinimumLabel = (facts: PriceFacts): LabelRef => ({
  id: LabelIds.FoodDrinkMinimum,
  args: facts.foodDrinkType != null ? [facts.foodDrinkType] : undefined,
});

/**
 * Collected-at-venue F&B caption (GB vs default).
 */
const collectedFoodDrinkCaption = (facts: PriceFacts): LabelRef | undefined => {
  if (facts.listingCountry === "GB") {
    return { id: LabelIds.FoodDrinkMinimumBeforeGratuityGb };
  }
  return { id: LabelIds.FoodDrinkMinimumBeforeGratuity };
};

/**
 * Day-rate discount label; percent passed as args when available.
 */
const dayRateDiscountLabel = (facts: PriceFacts): LabelRef => ({
  id: LabelIds.DayRateDiscount,
  args:
    facts.dayRateDiscountPercent != null
      ? [facts.dayRateDiscountPercent]
      : undefined,
});

/**
 * DIY: Service fee. FSV: Peerspace reservation fee.
 */
const hostFeeLabel = (facts: PriceFacts): LabelRef => ({
  id: facts.isDiy ? LabelIds.ServiceFee : LabelIds.PeerspaceReservationFee,
});

/**
 * DIY: Earnings. FSV: Estimated earnings.
 */
const earningsTotalLabel = (facts: PriceFacts): LabelRef => ({
  id: facts.isDiy ? LabelIds.Earnings : LabelIds.EstimatedEarnings,
});

/**
 * Entitlement uses BE-provided discount_str when present.
 */
const entitlementLabel = (facts: PriceFacts): LabelRef => ({
  id: LabelIds.Entitlement,
  args:
    facts.entitlement?.label != null ? [facts.entitlement.label] : undefined,
});

/**
 * Entitlement caption: BE-provided `discount_str`, shown under the title label.
 */
const entitlementCaption = (facts: PriceFacts): LabelRef | undefined => {
  if (facts.entitlement?.caption == null) return undefined;
  return { id: LabelIds.EntitlementCaption, args: [facts.entitlement.caption] };
};

/**
 * FSV due-at-venue / guest-spend right-side caption.
 */
const beforeGratuityValueCaption = (
  facts: PriceFacts,
): LabelRef | undefined => {
  if (facts.isDiy) return undefined;
  return { id: LabelIds.BeforeGratuity };
};

/**
 * Staff gratuity percent label.
 */
const staffGratuityLabel = (facts: PriceFacts): LabelRef => ({
  id: LabelIds.StaffGratuityPercent,
  args: [facts.gratuityRate ?? 0],
});

/**
 * Caption under space-rental rows when the listing has an attendee tier
 * (`{min}–{max}` guests). Bindings map `AttendeeRange` vs
 * `AttendeeRangeReadout` to dated vs hourly copy.
 */
const attendeeCaption =
  (labelId: LabelId) =>
  (facts: PriceFacts): LabelRef | undefined => {
    if (
      facts.attendeesMin === null ||
      facts.attendeesMax === null ||
      facts.attendeesMax <= 0
    ) {
      return undefined;
    }

    return { id: labelId, args: [facts.attendeesMin, facts.attendeesMax] };
  };

export {
  attendeeCaption,
  beforeGratuityValueCaption,
  bookingTotalCaption,
  bookingTotalLabel,
  collectedFoodDrinkCaption,
  dayRateDiscountLabel,
  diySpaceRentalLabel,
  diySpaceRentalRateLabel,
  earningsTotalLabel,
  entitlementCaption,
  entitlementLabel,
  foodDrinkMinimumLabel,
  fsvSpaceRentalLabel,
  hostFeeLabel,
  pendingOfferOffset,
  staffGratuityLabel,
};
